'use client';

import * as React from 'react';
import { gsap } from 'gsap';
import Link from 'next/link';
import NextImage from 'next/image';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import CmsImage from '@/components/ui/CmsImage';
import { scrollToHashTarget } from '@/components/util/GsapSmoothScroller';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import { localizeUrl, pickButton, stripHtml } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getStrapiOriginalMediaUrl, isStrapiVideoMedia } from '@/lib/strapi-media-url';
import type { BannerSlide, ButtonItem } from '@/types/homepage';
import { cn } from '@/utils/cn';
import CmsRichTextHeading, { hasRichTextContent } from '@/components/ui/CmsRichTextHeading';

interface HomeHeroSliderProps {
  /** Strapi banner slides. When omitted/empty, falls back to static slides. */
  slides?: BannerSlide[];
  /** Locale for CTA href prefixing. Defaults to `'en'`. */
  locale?: string;
  /**
   * When false, the secondary (outline) CTA is suppressed even if the slide
   * has one. Used by the About page hero where a single CTA is required.
   */
  showSecondaryCta?: boolean;
}

type ResolvedSlide = {
  id: string;
  title: string;
  titleRichText: string | null;
  description: string; // HTML
  mediaAlt: string;
  image: string;
  videoMime?: string;
  primaryCta: ButtonItem | null;
  secondaryCta: ButtonItem | null;
};

function hasText(value?: string | null): boolean {
  return Boolean(value?.trim());
}

function hasHtmlContent(html?: string | null): boolean {
  return hasText(stripHtml(html));
}

function isValidCta(cta: ButtonItem | null | undefined): cta is ButtonItem {
  if (!cta) return false;
  return hasText(cta.buttonText) || hasText(cta.buttonUrl);
}

function hasAnyCta(slide: ResolvedSlide, showSecondaryCta: boolean): boolean {
  return isValidCta(slide.primaryCta) || (showSecondaryCta && isValidCta(slide.secondaryCta));
}

function hasSlideContent(slide: ResolvedSlide, showSecondaryCta: boolean): boolean {
  return (
    hasRichTextContent(slide.titleRichText) ||
    hasText(slide.title) ||
    hasHtmlContent(slide.description) ||
    hasAnyCta(slide, showSecondaryCta)
  );
}

function slideMediaLabel(slide: ResolvedSlide): string {
  return slide.mediaAlt || slide.title || 'Wonder Cement';
}

const FALLBACK_SLIDES: ResolvedSlide[] = [
  {
    id: 'fallback-1',
    title: 'Wonder Laiye, Farq Nazar Ayega',
    titleRichText: null,
    description: '<p>Choose the heritage of quality that turns a house into a landmark</p>',
    mediaAlt: 'Wonder Cement hero',
    image: '/images/about_usbn.jpg',
    primaryCta: null,
    secondaryCta: null,
  },
  {
    id: 'fallback-2',
    title: 'Build with Trust, Build with Wonder',
    titleRichText: null,
    description: '<p>Precision engineering and durable strength for every foundation</p>',
    mediaAlt: 'Wonder Cement hero',
    image: '/images/about_usbn.jpg',
    primaryCta: null,
    secondaryCta: null,
  },
  {
    id: 'fallback-3',
    title: 'German Precision, Indian Legacy',
    titleRichText: null,
    description: '<p>Performance that delivers confidence from start to finish</p>',
    mediaAlt: 'Wonder Cement hero',
    image: '/images/about_usbn.jpg',
    primaryCta: null,
    secondaryCta: null,
  },
];

const DEFAULT_PRIMARY_TEXT = 'Enquire Now';
const DEFAULT_SECONDARY_TEXT = 'Start Your Journey';
const SLIDE_INTERVAL_MS = 7000;
const FIRST_VISIT_LOADER_BODY_CLASS = 'first-visit-loader-active';

function smoothScrollToHash(hash: string): boolean {
  return scrollToHashTarget(hash, true, true);
}

function resolveSlides(slides: BannerSlide[]): ResolvedSlide[] {
  const valid = slides
    .filter((s) => s.image)
    .map<ResolvedSlide | null>((s) => {
      const imageUrl = getStrapiOriginalMediaUrl(s.image);
      if (!imageUrl) return null;
      const isVideo = isStrapiVideoMedia(s.image);
      const primaryCta = pickButton(s.buttonDark, 'Solid') ?? null;
      const secondaryCta = s.buttonWhite[0] ?? null;
      return {
        id: `slide-${s.id}`,
        title: s.title?.trim() ?? '',
        titleRichText: s.titleRichText ?? null,
        description: s.description ?? '',
        mediaAlt: s.image?.alternativeText?.trim() ?? '',
        image: imageUrl,
        videoMime: isVideo ? s.image?.mime || 'video/mp4' : undefined,
        primaryCta: isValidCta(primaryCta) ? primaryCta : null,
        secondaryCta: isValidCta(secondaryCta) ? secondaryCta : null,
      };
    })
    .filter((s): s is ResolvedSlide => s !== null);

  return valid.length > 0 ? valid : FALLBACK_SLIDES;
}

/**
 * Resolve the `<video preload>` hint. Inactive slides skip loading entirely;
 * the priority (first) slide preloads its full source, others just metadata.
 */
function resolveVideoPreload(shouldLoad: boolean, priority: boolean): 'auto' | 'metadata' | 'none' {
  if (!shouldLoad) return 'none';
  return priority ? 'auto' : 'metadata';
}

/** Pause `video` unless it is both requested to play and currently visible. */
function playVisibleVideo(video: HTMLVideoElement, shouldPlay: boolean) {
  if (shouldPlay && video.offsetParent !== null) {
    video.muted = true;
    void video.play().catch(() => {});
  } else {
    video.pause();
  }
}

type HeroVideoElementProps = {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  src: string;
  mime: string;
  media: string;
  slideIndex: number;
  ariaLabel: string;
  priority: boolean;
  shouldLoad: boolean;
  ready: boolean;
  className: string;
  onReady: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
  onNotReady: () => void;
};

/** The shared `<video>` element used by both the mobile and desktop layouts. */
function HeroVideoElement({
  videoRef,
  src,
  mime,
  media,
  slideIndex,
  ariaLabel,
  priority,
  shouldLoad,
  ready,
  className,
  onReady,
  onNotReady,
}: HeroVideoElementProps) {
  return (
    <video
      ref={videoRef}
      data-hero-video
      data-slide-index={slideIndex}
      autoPlay
      muted
      loop
      playsInline
      controls={false}
      disablePictureInPicture
      preload={resolveVideoPreload(shouldLoad, priority)}
      poster={MEDIA_PLACEHOLDER}
      aria-label={ariaLabel}
      className={cn(className, ready ? 'opacity-100' : 'opacity-0')}
      onLoadedData={onReady}
      onCanPlay={onReady}
      onPlaying={onReady}
      onError={onNotReady}
    >
      <source src={shouldLoad ? src : undefined} type={mime} media={media} />
    </video>
  );
}

/** Hero video with brand placeholder visible until the first frame is ready. */
function HeroVideo({
  src,
  mime,
  slideIndex,
  ariaLabel,
  priority = false,
  mobile = false,
  shouldLoad = true,
  shouldPlay = false,
}: {
  src: string;
  mime: string;
  slideIndex: number;
  ariaLabel: string;
  priority?: boolean;
  mobile?: boolean;
  shouldLoad?: boolean;
  shouldPlay?: boolean;
}) {
  const [ready, setReady] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);

  React.useEffect(() => {
    const video = videoRef.current;

    // A carousel video can finish loading while its slide is inactive, before
    // React receives a later `loadeddata`/`canplay` event. Read the browser's
    // current state as well so an already-decoded frame replaces the
    // placeholder as soon as the slide is shown.
    if (!video || !shouldLoad) {
      video?.pause();
      setReady(false);
      return;
    }

    setReady(video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA);
    video.load();
  }, [src, shouldLoad]);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    playVisibleVideo(video, shouldPlay);
  }, [shouldPlay]);

  const markReady = React.useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      setReady(true);
      // Mobile Safari can reject an early play() while the source is still
      // loading. Retry when a decoded frame is actually available.
      playVisibleVideo(event.currentTarget, shouldPlay);
    },
    [shouldPlay],
  );

  const notReady = React.useCallback(() => setReady(false), []);

  const videoElement = (
    <HeroVideoElement
      videoRef={videoRef}
      src={src}
      mime={mime}
      media={mobile ? '(max-width: 767px)' : '(min-width: 768px)'}
      slideIndex={slideIndex}
      ariaLabel={ariaLabel}
      priority={priority}
      shouldLoad={shouldLoad}
      ready={ready}
      className={
        mobile
          ? 'pointer-events-none absolute inset-0 aspect-video h-full w-full object-cover transition-opacity duration-500'
          : 'pointer-events-none absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-500'
      }
      onReady={markReady}
      onNotReady={notReady}
    />
  );

  if (mobile) {
    return (
      <span className="relative block aspect-video w-full overflow-hidden">
        {!ready ? (
          <NextImage
            src={MEDIA_PLACEHOLDER}
            alt=""
            aria-hidden
            width={1920}
            height={1080}
            priority={priority}
            unoptimized
            className="h-auto w-full object-cover"
            sizes="100vw"
          />
        ) : null}
        {videoElement}
      </span>
    );
  }

  return (
    <>
      {!ready ? (
        <NextImage
          src={MEDIA_PLACEHOLDER}
          alt=""
          aria-hidden
          fill
          priority={priority}
          unoptimized
          className="object-cover object-center"
          sizes="100vw"
        />
      ) : null}
      {videoElement}
    </>
  );
}

function CtaButton({
  cta,
  defaultText,
  locale,
  variant,
  showArrow,
}: {
  cta: ButtonItem | null;
  defaultText: string;
  locale: string;
  variant: 'primary' | 'secondary';
  showArrow?: boolean;
}) {
  if (!cta) {
    return null;
  }
  const text = cta.buttonText || defaultText;
  const ariaLabel = cta?.ariaLabel || undefined;
  const href = localizeUrl(cta.buttonUrl, locale);
  const linkProps = getCmsButtonLinkProps(cta);
  const className =
    variant === 'primary'
      ? '!min-h-[40px] !w-full !justify-center !rounded-md !px-3.5 !py-2 text-xs text-black hover:bg-white hover:text-black sm:!min-h-[44px] sm:!w-auto sm:!px-4 sm:!py-2.5 sm:text-sm'
      : '!min-h-[40px] !w-full !justify-center !rounded-md !border !border-white !bg-transparent !px-3.5 !py-2 text-xs !text-white hover:bg-white hover:text-black sm:!min-h-[44px] sm:!w-auto sm:!px-4 sm:!py-2.5 sm:text-sm';
  // Default: only the secondary CTA gets the swap-arrow. Callers can override
  // (e.g. about-us hides the secondary CTA, so the primary becomes the lone
  // call-to-action and should carry the arrow itself).
  const resolvedShowArrow = showArrow ?? variant === 'secondary';

  if (href) {
    if (href.startsWith('#')) {
      return (
        <a
          href={href}
          {...linkProps}
          aria-label={ariaLabel}
          className="block w-full sm:w-auto"
          onClick={
            linkProps.target === '_blank'
              ? undefined
              : (event) => {
                  event.preventDefault();
                  smoothScrollToHash(href);
                }
          }
        >
          <SwapTextButton text={text} showArrow={resolvedShowArrow} className={className} />
        </a>
      );
    }

    return (
      <Link href={href} {...linkProps} aria-label={ariaLabel} className="block w-full sm:w-auto">
        <SwapTextButton text={text} showArrow={resolvedShowArrow} className={className} />
      </Link>
    );
  }
  return <SwapTextButton text={text} showArrow={resolvedShowArrow} className={className} />;
}

function syncHeroVideos(root: HTMLElement, slideIndex: number) {
  root.querySelectorAll<HTMLVideoElement>('video[data-hero-video]').forEach((video) => {
    const isActive = Number(video.dataset.slideIndex) === slideIndex;
    // The mobile and desktop layouts each render the slide's <video>, and a
    // `display:none` (`hidden`/`md:hidden`) video still plays — so without a
    // visibility gate both twins would run. `offsetParent === null` is true
    // only for display:none nodes; play just the visible active slide.
    const isVisible = video.offsetParent !== null;
    const shouldPlay = isActive && isVisible;
    video.muted = true;
    if (shouldPlay) {
      void video.play().catch(() => {});
    } else {
      video.pause();
    }
  });
}

function HeroCtaRow({
  slide,
  locale,
  showSecondaryCta,
  hasTitleOrDescription,
}: {
  slide: ResolvedSlide;
  locale: string;
  showSecondaryCta: boolean;
  hasTitleOrDescription: boolean;
}) {
  return (
    <div
      className={cn(
        'flex max-w-[22rem] flex-col gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:gap-4',
        hasTitleOrDescription ? 'mt-6 sm:mt-8 md:mt-10' : null,
      )}
    >
      {isValidCta(slide.primaryCta) ? (
        <div data-hero-content-item className="min-w-0">
          <CtaButton
            cta={slide.primaryCta}
            defaultText={DEFAULT_PRIMARY_TEXT}
            locale={locale}
            variant="primary"
            showArrow={!showSecondaryCta}
          />
        </div>
      ) : null}
      {showSecondaryCta && isValidCta(slide.secondaryCta) ? (
        <div data-hero-content-item className="min-w-0">
          <CtaButton
            cta={slide.secondaryCta}
            defaultText={DEFAULT_SECONDARY_TEXT}
            locale={locale}
            variant="secondary"
          />
        </div>
      ) : null}
    </div>
  );
}

function HeroDots({
  slides,
  activeIndex,
  onSelect,
  hasContent,
  isAutoplaying,
}: {
  slides: ResolvedSlide[];
  activeIndex: number;
  onSelect: (index: number) => void;
  hasContent: boolean;
  isAutoplaying: boolean;
}) {
  return (
    <div
      className={cn(
        'container relative z-20 mx-auto flex w-full justify-center gap-1 px-4 md:absolute md:bottom-8 md:left-1/2 md:-translate-x-1/2 md:px-6 md:py-0 lg:bottom-10 lg:px-0',
        hasContent ? 'py-4 max-md:pb-6' : 'max-md:py-0',
      )}
    >
      {slides.map((slide, index) => (
        <button
          key={slide.id}
          type="button"
          onClick={() => onSelect(index)}
          aria-label={`Go to slide ${index + 1}`}
          aria-current={activeIndex === index ? 'true' : undefined}
          className="flex min-h-11 min-w-11 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        >
          <span
            className={`relative block h-[3px] overflow-hidden rounded-full transition-all ${
              activeIndex === index ? 'w-9 bg-white/35' : 'w-6 bg-white/35'
            }`}
          >
            {activeIndex === index && isAutoplaying ? (
              <span
                className="absolute inset-y-0 left-0 w-full origin-left bg-white"
                style={{ animation: `heroDotProgress ${SLIDE_INTERVAL_MS}ms linear forwards` }}
              />
            ) : null}
          </span>
        </button>
      ))}
    </div>
  );
}

/** One desktop full-bleed media layer (video or image), cross-faded by GSAP. */
function HeroDesktopMediaLayer({
  slide,
  index,
  shouldPlay,
  registerRef,
}: {
  slide: ResolvedSlide;
  index: number;
  shouldPlay: boolean;
  registerRef: (index: number, element: HTMLDivElement | null) => void;
}) {
  return (
    <div
      ref={(element) => registerRef(index, element)}
      className={`absolute inset-0 ${index === 0 ? 'opacity-100' : 'opacity-0'}`}
    >
      {slide.videoMime ? (
        <HeroVideo
          src={slide.image}
          mime={slide.videoMime}
          slideIndex={index}
          ariaLabel={slideMediaLabel(slide)}
          priority={index === 0}
          shouldPlay={shouldPlay}
        />
      ) : (
        <CmsImage
          src={slide.image}
          alt={slideMediaLabel(slide)}
          fill
          priority={index === 0}
          loading="eager"
          fetchPriority={index === 0 ? 'high' : 'low'}
          className="object-cover object-center"
          sizes="(min-width: 1920px) 1920px, 100vw"
        />
      )}
    </div>
  );
}

/** Animate the active slide's content items in (respecting reduced-motion). */
function runHeroContentAnimation(contentRoot: HTMLDivElement | null) {
  const sequence = contentRoot
    ? Array.from(contentRoot.querySelectorAll<HTMLElement>('[data-hero-content-item]'))
    : [];
  if (!sequence.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    gsap.set(sequence, { clearProps: 'opacity,transform' });
    return;
  }

  gsap.killTweensOf(sequence);
  gsap.set(sequence, { y: 26, opacity: 0 });
  gsap.to(sequence, {
    y: 0,
    opacity: 1,
    duration: 0.72,
    delay: 0.14,
    ease: 'power2.out',
    stagger: 0.1,
  });
}

export default function HomeHeroSlider({
  slides,
  locale = 'en',
  showSecondaryCta = true,
}: HomeHeroSliderProps = {}) {
  const resolved = React.useMemo(() => resolveSlides(slides ?? []), [slides]);

  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isPageLoaded, setIsPageLoaded] = React.useState(false);
  const [isPageVisible, setIsPageVisible] = React.useState(false);
  const [isPageRevealed, setIsPageRevealed] = React.useState(false);
  const [isBannerVisible, setIsBannerVisible] = React.useState(false);
  const [hasUserEngaged, setHasUserEngaged] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const imageWrapRef = React.useRef<HTMLDivElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const imageRefs = React.useRef<Array<HTMLDivElement | null>>([]);
  const activeIndexRef = React.useRef(activeIndex);
  activeIndexRef.current = activeIndex;

  const totalSlides = resolved.length;
  const activeSlide = resolved[activeIndex] ?? resolved[0];
  const activeSlideHasContent = hasSlideContent(activeSlide, showSecondaryCta);
  const activeSlideHasTitle =
    hasRichTextContent(activeSlide.titleRichText) || hasText(activeSlide.title);
  const activeSlideHasDescription = hasHtmlContent(activeSlide.description);
  const activeSlideHasCtas = hasAnyCta(activeSlide, showSecondaryCta);
  const canAutoplay =
    hasUserEngaged && isPageLoaded && isPageVisible && isPageRevealed && isBannerVisible;

  React.useEffect(() => {
    if (activeIndex < totalSlides) return;
    setActiveIndex(0);
  }, [activeIndex, totalSlides]);

  React.useEffect(() => {
    const updatePageVisibility = () => setIsPageVisible(document.visibilityState === 'visible');
    const markPageLoaded = () => setIsPageLoaded(true);

    updatePageVisibility();
    if (document.readyState === 'complete') {
      markPageLoaded();
    } else {
      window.addEventListener('load', markPageLoaded, { once: true });
    }
    document.addEventListener('visibilitychange', updatePageVisibility);

    return () => {
      window.removeEventListener('load', markPageLoaded);
      document.removeEventListener('visibilitychange', updatePageVisibility);
    };
  }, []);

  React.useEffect(() => {
    if (hasUserEngaged) return;

    const markEngaged = () => setHasUserEngaged(true);
    const eventOptions: AddEventListenerOptions = { once: true, passive: true };

    window.addEventListener('pointerdown', markEngaged, eventOptions);
    window.addEventListener('touchstart', markEngaged, eventOptions);
    window.addEventListener('keydown', markEngaged, { once: true });

    return () => {
      window.removeEventListener('pointerdown', markEngaged);
      window.removeEventListener('touchstart', markEngaged);
      window.removeEventListener('keydown', markEngaged);
    };
  }, [hasUserEngaged]);

  React.useEffect(() => {
    const updatePageReveal = () => {
      setIsPageRevealed(!document.body.classList.contains(FIRST_VISIT_LOADER_BODY_CLASS));
    };

    updatePageReveal();
    const observer = new MutationObserver(updatePageReveal);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    if (!('IntersectionObserver' in window)) {
      setIsBannerVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsBannerVisible(Boolean(entry?.isIntersecting)),
      { threshold: 0.01 },
    );
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const syncVideos = (slideIndex: number) => syncHeroVideos(root, canAutoplay ? slideIndex : -1);

    syncVideos(activeIndex);
    // Crossing the md breakpoint swaps which layout is visible; re-sync so the
    // newly visible twin plays instead of the now-hidden one.
    const onResize = () => syncVideos(activeIndexRef.current);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [activeIndex, canAutoplay, resolved]);

  // Auto-advance through slides — pauses naturally on a single-slide carousel.
  React.useEffect(() => {
    if (!canAutoplay || totalSlides <= 1) return;
    const timer = window.setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % totalSlides);
    }, SLIDE_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [activeIndex, canAutoplay, totalSlides]);

  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) return;
    if (!imageWrapRef.current) return;
    const images = imageRefs.current.filter(Boolean) as HTMLDivElement[];
    if (!images.length) return;
    gsap.killTweensOf(images);
    gsap.to(images, { opacity: 0, duration: 0.65, ease: 'sine.inOut' });
    const active = imageRefs.current[activeIndex];
    if (active) {
      gsap.to(active, {
        opacity: 1,
        duration: 0.9,
        delay: 0.05,
        ease: 'sine.inOut',
      });
    }
  }, [activeIndex]);

  React.useEffect(() => {
    if (!activeSlideHasContent) return;
    runHeroContentAnimation(contentRef.current);
  }, [activeIndex, activeSlideHasContent]);

  return (
    <div ref={rootRef} className="relative">
      <article className="relative flex flex-col bg-black max-md:min-h-0 max-md:overflow-visible md:block md:h-[700px] md:min-h-[100dvh] md:overflow-hidden">
        {/* Mobile / small tablet: stable 16:9 media; content follows in document flow. */}
        <div className="relative aspect-video w-full shrink-0 overflow-hidden md:hidden">
          {resolved.map((slide, index) => (
            <div
              key={slide.id}
              aria-hidden={index !== activeIndex}
              className={cn(
                'absolute inset-0 transition-opacity duration-500',
                index === activeIndex ? 'z-[1] opacity-100' : 'pointer-events-none opacity-0',
              )}
            >
              {slide.videoMime ? (
                <HeroVideo
                  src={slide.image}
                  mime={slide.videoMime}
                  slideIndex={index}
                  ariaLabel={slideMediaLabel(slide)}
                  priority={index === 0}
                  mobile
                  shouldPlay={canAutoplay && index === activeIndex}
                />
              ) : (
                <CmsImage
                  src={slide.image}
                  alt={slideMediaLabel(slide)}
                  fill
                  priority={index === 0}
                  loading="eager"
                  fetchPriority={index === 0 ? 'high' : 'low'}
                  className="object-cover object-center"
                  sizes="(min-width: 1920px) 1920px, 100vw"
                />
              )}
            </div>
          ))}
        </div>

        {/* Desktop: full-bleed layers + gradient (unchanged behaviour). */}
        <div ref={imageWrapRef} className="absolute inset-0 hidden md:block">
          {resolved.map((slide, index) => (
            <HeroDesktopMediaLayer
              key={slide.id}
              slide={slide}
              index={index}
              shouldPlay={canAutoplay && index === activeIndex}
              registerRef={(i, element) => {
                imageRefs.current[i] = element;
              }}
            />
          ))}
          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.2)_0%,rgba(0,0,0,0.62)_38%,rgba(0,0,0,0.84)_100%)] transition-opacity duration-500 md:bg-[linear-gradient(90deg,rgba(0,0,0,0.78)_0%,rgba(0,0,0,0.54)_38%,rgba(0,0,0,0.05)_100%)]',
              activeSlideHasContent ? 'opacity-100' : 'opacity-0',
            )}
          />
        </div>

        {activeSlideHasContent ? (
          <div className="container relative z-10 mx-auto flex w-full flex-col px-4 py-8 max-md:bg-[#0a0a0a] sm:px-6 sm:py-10 md:h-full md:min-h-[420px] md:bg-transparent md:px-6 md:py-16 lg:min-h-[500px] lg:px-0 lg:py-20">
            <div
              ref={contentRef}
              className="mb-20 mt-auto max-w-full text-white max-md:mb-0 max-md:mt-0 sm:mb-16 sm:max-w-[28rem] md:my-auto md:max-w-[30rem] lg:max-w-[34rem]"
              key={activeSlide.id}
            >
              {activeSlideHasTitle ? (
                // Only the active slide's content is mounted, so keeping its title as
                // <h1> guarantees the homepage retains exactly one primary heading
                // throughout carousel autoplay and manual slide navigation.
                <CmsRichTextHeading
                  data-hero-content-item
                  className="hero-title whitespace-pre-line text-white"
                  html={activeSlide.titleRichText}
                  fallback={activeSlide.title}
                />
              ) : null}
              {activeSlideHasDescription ? (
                <div
                  data-hero-content-item
                  className={cn(
                    'hero-subtitle max-w-full text-white/85 sm:max-w-[32rem] [&_p]:m-0',
                    activeSlideHasTitle ? 'mt-4 sm:mt-5' : null,
                  )}
                  dangerouslySetInnerHTML={{ __html: activeSlide.description }}
                />
              ) : null}
              {activeSlideHasCtas ? (
                <HeroCtaRow
                  slide={activeSlide}
                  locale={locale}
                  showSecondaryCta={showSecondaryCta}
                  hasTitleOrDescription={activeSlideHasTitle || activeSlideHasDescription}
                />
              ) : null}
            </div>
          </div>
        ) : null}

        <HeroDots
          slides={resolved}
          activeIndex={activeIndex}
          onSelect={setActiveIndex}
          hasContent={activeSlideHasContent}
          isAutoplaying={canAutoplay && totalSlides > 1}
        />
      </article>
    </div>
  );
}
